import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, ShieldCheck, AlertTriangle, CheckCircle2, 
  XCircle, ChevronRight, Ship, Anchor, Clock, Award
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComplianceResultPage() {
  const navigate = useNavigate();
  const { checklistId } = useParams();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startingTrip, setStartingTrip] = useState(false);

  useEffect(() => {
    fetchChecklist();
  }, [checklistId]);

  const fetchChecklist = async () => {
    try {
      const response = await axios.get(`${API}/checklists/${checklistId}`, { withCredentials: true });
      setChecklist(response.data);
    } catch (err) {
      console.error('Failed to load checklist:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrip = async () => {
    setStartingTrip(true);
    try {
      await axios.post(`${API}/trips`, {
        checklist_id: checklistId
      }, { withCredentials: true });
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to start trip:', err);
    } finally {
      setStartingTrip(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#2B3A67] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-[#D32F2F] font-bold">Checklist not found</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const score = checklist.compliance_score;
  const isCompliant = checklist.is_compliant;
  const isVerified = checklist.is_verified;

  const verifiedItems = checklist.required_items.filter(i => i.status === 'verified');
  const missingItems = checklist.required_items.filter(i => i.status !== 'verified');
  const recommendedItems = checklist.recommended_items;

  const getScoreColor = () => {
    if (score === 100) return 'text-[#3CB371]';
    if (score >= 70) return 'text-[#E5A000]';
    return 'text-[#D32F2F]';
  };

  const getScoreBg = () => {
    if (score === 100) return 'bg-[#E8F6ED] border-[#3CB371]';
    if (score >= 70) return 'bg-[#FDF3E1] border-[#E5A000]';
    return 'bg-[#FAEAEA] border-[#D32F2F]';
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8] pb-32 page-enter">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b-2 border-[#E2E8F0] px-6 py-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              data-testid="result-back-btn"
              onClick={() => navigate(`/checklist/${checklistId}`)}
              className="text-[#2B3A67]"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-bold text-[#2B3A67] font-['Outfit']">Compliance Result</h1>
            <div className="w-10"></div>
          </div>
        </div>
      </header>

      <main className="px-6 py-6 max-w-lg mx-auto space-y-6">
        {/* Score Card */}
        <Card className={`border-2 ${getScoreBg()} shadow-lg`}>
          <CardContent className="p-6 text-center">
            {/* Verified Badge */}
            {isVerified && (
              <div className="verified-badge inline-flex items-center gap-2 bg-[#E8F6ED] text-[#2A8A53] border border-[#3CB371]/30 px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide mb-4">
                <ShieldCheck className="w-5 h-5" />
                Verified Pre-Departure Safety Check
              </div>
            )}

            {/* Score */}
            <div className="mb-4">
              <p data-testid="compliance-score" className={`text-7xl font-black ${getScoreColor()} font-['Outfit']`}>
                {score}%
              </p>
              <p className="text-lg font-medium text-[#4A5568]">Compliance Score</p>
            </div>

            {/* Status Message */}
            <div className={`p-4 rounded-xl ${isCompliant ? 'bg-white' : 'bg-white'}`}>
              {isCompliant ? (
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[#3CB371]" />
                  <p className="font-bold text-[#2A8A53]">
                    You are compliant for Florida FWC requirements
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-[#D32F2F]" />
                  <p className="font-bold text-[#D32F2F]">
                    You are NOT compliant — missing critical safety items
                  </p>
                </div>
              )}
            </div>

            {/* Vessel Info */}
            <div className="flex items-center justify-center gap-4 mt-4 text-sm text-[#4A5568]">
              <div className="flex items-center gap-1">
                <Ship className="w-4 h-4" />
                <span>{checklist.vessel_name}</span>
              </div>
              <div className="flex items-center gap-1">
                <Anchor className="w-4 h-4" />
                <span>Class {checklist.vessel_class}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span className="capitalize">{checklist.time_of_day}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Required Items Met */}
        {verifiedItems.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-[#2B3A67] font-['Outfit'] mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#3CB371]" />
              Required Items Met ({verifiedItems.length})
            </h2>
            <Card className="border-2 border-[#E2E8F0]">
              <CardContent className="p-4 space-y-3">
                {verifiedItems.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`verified-item-${item.id}`}
                    className="flex items-center gap-3 p-3 bg-[#E8F6ED] rounded-xl"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#3CB371] flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-[#2A8A53]">{item.name}</p>
                      {item.count && (
                        <p className="text-sm text-[#2A8A53]">Count: {item.count}</p>
                      )}
                    </div>
                    {item.photo_id && (
                      <span className="text-xs font-bold text-[#3CB371] bg-white px-2 py-1 rounded">
                        📷 Photo
                      </span>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Missing Required Items */}
        {missingItems.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-[#2B3A67] font-['Outfit'] mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-[#D32F2F]" />
              Missing Required Items ({missingItems.length})
            </h2>
            <Card className="border-2 border-[#D32F2F]/30">
              <CardContent className="p-4 space-y-3">
                {missingItems.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`missing-item-${item.id}`}
                    className="flex items-center gap-3 p-3 bg-[#FAEAEA] rounded-xl"
                  >
                    <XCircle className="w-5 h-5 text-[#D32F2F] flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-[#D32F2F]">{item.name}</p>
                      <p className="text-sm text-[#D32F2F]/80">{item.description}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recommended Items */}
        {recommendedItems.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-[#2B3A67] font-['Outfit'] mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#E5A000]" />
              Recommended Items
            </h2>
            <Card className="border-2 border-[#E2E8F0]">
              <CardContent className="p-4 space-y-3">
                {recommendedItems.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-xl ${
                      item.status === 'verified' ? 'bg-[#E8F6ED]' : 'bg-[#FDF3E1]'
                    }`}
                  >
                    {item.status === 'verified' ? (
                      <CheckCircle2 className="w-5 h-5 text-[#3CB371] flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-[#E5A000] flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className={`font-medium ${item.status === 'verified' ? 'text-[#2A8A53]' : 'text-[#E5A000]'}`}>
                        {item.name}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Bottom CTAs */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#E2E8F0] px-6 py-4">
        <div className="max-w-lg mx-auto space-y-3">
          {isCompliant && (
            <Button
              data-testid="view-certificate-btn"
              onClick={() => navigate(`/certificate/view/${checklistId}`)}
              variant="outline"
              className="w-full h-12 border-2 border-[#3CB371] text-[#3CB371] font-bold rounded-xl"
            >
              <Award className="w-5 h-5 mr-2" />
              View & Share Certificate
            </Button>
          )}
          <div className="flex gap-3">
            {!isCompliant && (
              <Button
                data-testid="fix-issues-btn"
                variant="outline"
                onClick={() => navigate(`/checklist/${checklistId}`)}
                className="flex-1 h-14 border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-lg rounded-xl"
              >
                Fix Issues
              </Button>
            )}
            <Button
              data-testid="start-trip-btn"
              onClick={handleStartTrip}
              disabled={startingTrip}
              className={`${isCompliant ? 'w-full' : 'flex-1'} h-14 font-bold text-lg rounded-xl transition-all active:scale-[0.98] ${
                isCompliant
                  ? 'bg-[#3CB371] hover:bg-[#2A8A53] text-white'
                  : 'bg-[#E5A000] hover:bg-[#CC8F00] text-white'
              }`}
            >
              {startingTrip ? 'Starting...' : isCompliant ? (
                <>
                  <Ship className="w-5 h-5 mr-2" />
                  Start Trip
                </>
              ) : 'Start Trip Anyway'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
